import { OmsRecycleOrderDetail } from 'types/oms';
import { UmsMember } from 'types/ums';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchOmsRecyclingList, fetchRecycleOrderDetail } from 'apis/oms';
import usePagination from 'commons/hooks/usePagination';
import { PageQuery, UnwrapPromise } from 'types/base';
import Table, { ColumnsType } from 'antd/es/table';
import showTotal from 'components/show-total';
import { Button, message, DatePicker, Input, Card, Select } from 'antd';
import {
  OrderType,
  ShopMaping,
  getRecycleOrderStatusText,
  OMS_RECYCLE_ORDER_STATUS_MAP,
  StatusListDefault,
  storeSource,
} from 'constants/RecyclingConsignment';
import { useNavigate } from 'react-router-dom';
import i18n from '../../../i18n';
import LOCALS from '../../../commons/locals';
import {
  getDempyouProduct,
  getDempyouOrderInfo,
  getDempyouToPrint,
  ProductType,
  PrintParamType,
  getDempyouLang,
} from 'utils/getDempyouParam';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import dayjs from 'dayjs';
import classNames from 'classnames';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import { thousands } from 'utils/tools';
import { Trans } from 'react-i18next';
import { getMemberByIdV2 } from 'apis/ums';
import getProductDisplayInfo, {
  buildProductNameWithAttributeRemarks,
} from 'utils/get-product-display-info';
import LinkButton from 'components/link-button';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import { Printer, ShoppingBag } from 'lucide-react';
import { chunk } from 'lodash-es';
import {
  findLabelByValue,
  PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
} from 'commons/options';

const RecyclingConsignmentHistory = ({
  memberId,
  member,
}: {
  memberId: UmsMember['id'];
  member: UnwrapPromise<ReturnType<typeof getMemberByIdV2>>;
}) => {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<OmsRecycleOrderDetail[]>([]);
  const [submissionId, setSubmissionId] = useState<number | undefined>(
    undefined,
  );
  const [storeId, setStoreId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<string>('all');
  const {
    staffSelectOptions,
    countryOptions,
    productCategoryCascaderOptions,
    colorSelectOptions,
    rankSelectOptions,
    stampSelectOptions,
    materialCascaderOptionsMap,
    hardwareSelectOptions,
    accessorySelectOptions,
  } = useAppSelector(selectGlobalInfo);
  const { userId } = useAppSelector(selectUserInfo);

  const {
    loading,
    setLoading,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
    dataSource,
    setDataSource,
  } = usePagination<OmsRecycleOrderDetail>();

  const [beginCommitTime, setBeginCommitTime] = useState('');
  const [endCommitTime, setEndCommitTime] = useState('');

  const getDataSource = useCallback(
    async ({
      pageNum,
      pageSize,
      status,
      storeId: storeIdParam,
      beginCommitTime: beginCommitTimeParam,
      endCommitTime: endCommitTimeParam,
      submissionId: submissionIdParam,
    }: PageQuery & {
      status?: string;
      storeId?: number;
      beginCommitTime?: string;
      endCommitTime?: string;
      submissionId?: number;
    }) => {
      try {
        setLoading(true);
        const {
          data: { list, total },
        } = await fetchOmsRecyclingList({
          memberId,
          pageNum,
          pageSize,
          status,
          beginCommitTime: beginCommitTimeParam ?? beginCommitTime,
          endCommitTime: endCommitTimeParam ?? endCommitTime,
          submissionId: submissionIdParam ?? submissionId,
          storeIds:
            (storeIdParam ?? storeId) !== undefined
              ? [storeIdParam ?? storeId!]
              : undefined,
        });
        setDataSource(list);
        setTotal(total || 0);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    },
    [
      beginCommitTime,
      endCommitTime,
      memberId,
      setDataSource,
      setLoading,
      setTotal,
      submissionId,
      storeId,
    ],
  );

  useEffect(() => {
    getDataSource({ pageNum: 1, pageSize: 10 });
  }, [getDataSource]);

  /** 渲染商品信息 */
  const ProductShowData = useCallback(
    (data: OmsRecycleOrderDetail): React.ReactNode => {
      const { omsRecycleOrderProduct } = data;
      const {
        productCategoryId = 0,
        attrAccessory,
        attrColor,
        attrStamp,
        attrMaterial,
        attrHardware,
        name,
        stockStatus,
      } = omsRecycleOrderProduct || {};
      // 配件
      const accessory: string[] = [];
      if (attrAccessory) {
        attrAccessory.split(',').forEach((d: string) => {
          const t = accessorySelectOptions.find((dd) => dd.value === d);
          if (t) accessory.push(t.label);
        });
      }
      // color
      const color: string[] = [];
      attrColor &&
        attrColor.split(',')?.forEach((d: string) => {
          const t = colorSelectOptions.find((dd) => dd.value === d);
          if (t) color.push(t.label);
        });
      // material
      const material: string[] = [];
      const target = findCascaderOptionById(
        productCategoryId,
        productCategoryCascaderOptions,
      );
      const productCategoryIds = target?.treeIds?.split(',')?.map(Number) || [];
      const mateList = materialCascaderOptionsMap[productCategoryIds[0]] || [];
      attrMaterial &&
        attrMaterial.split(',')?.forEach((d: string) => {
          const t = findCascaderOptionById(d, mateList);
          if (t) material.push(t.label);
        });
      // hardware
      const hardware: string[] = [];
      attrHardware &&
        attrHardware.split(',')?.forEach((d: string) => {
          const t = hardwareSelectOptions.find((dd) => dd.value === d);
          if (t) hardware.push(t.label);
        });

      // 商品名、素材、色、刻印、金具
      const info1 = (
        <div
          className={classNames('whitespace-pre-line', 'flex flex-col gap-2')}
        >
          {chunk(
            [
              name || '',
              material.join('、') || '',
              color.join('、') || '',
              stampSelectOptions.find((d) => d.value === attrStamp)?.label,
              hardware.join('、') || '',
            ].filter((i) => !!i),
            3,
          ).map((i, index) => (
            <div key={index} className="flex gap-2">
              {i.map((j, index) => (
                <div key={index}>{j}</div>
              ))}
            </div>
          ))}

          <div>
            {findLabelByValue(
              stockStatus,
              PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
            )}
          </div>
        </div>
      );

      const t = info1;
      return <div>{t}</div>;
    },
    [
      materialCascaderOptionsMap,
      productCategoryCascaderOptions,
      accessorySelectOptions,
      colorSelectOptions,
      stampSelectOptions,
      hardwareSelectOptions,
    ],
  );

  const columns: ColumnsType<OmsRecycleOrderDetail> = useMemo(() => {
    return [
      {
        title: i18n.t(LOCALS.sHXnOEVSrC),
        key: 'submissionId',
        dataIndex: 'submissionId',
        fixed: 'left',
        render: (_: OmsRecycleOrderDetail, record: OmsRecycleOrderDetail) => {
          if (!record) return;
          return record.omsRecycleOrder?.submissionId;
        },
      },
      {
        title: i18n.t(LOCALS.code),
        key: 'code',
        dataIndex: 'code',
        render: (_: OmsRecycleOrderDetail, record: OmsRecycleOrderDetail) => {
          if (!record) return;
          return (
            <Button
              type="link"
              onClick={() => {
                navigate(
                  `/rrs/recycling-consignment-detail/${record?.omsRecycleOrder?.id}`,
                );
              }}
            >
              {record.omsRecycleOrder?.code}
            </Button>
          );
        },
      },

      {
        title: <Trans i18nKey={LOCALS.associated_product_id} />,
        key: 'product_link',
        render: (record: OmsRecycleOrderDetail) => {
          if (!record) return;
          const productId = record.omsRecycleOrderItem?.productId;

          if (!productId) return '-';

          return (
            <LinkButton href={`/pms/product-edit/${productId}`}>
              {productId}
            </LinkButton>
          );
        },
      },
      {
        title: i18n.t(LOCALS.product_info),
        key: 'name',
        dataIndex: 'name',
        render: (_: string, record: OmsRecycleOrderDetail) => {
          if (!record) return;
          return ProductShowData(record);
        },
      },
      {
        title: i18n.t(LOCALS.order_type),
        key: 'type',
        dataIndex: 'type',
        render: (_: string, record: OmsRecycleOrderDetail) => {
          if (!record) return;
          let t = OrderType.find(
            (d) => record.omsRecycleOrder?.type === d.value,
          );
          if (t) {
            return t.label;
          }
        },
      },
      {
        title: i18n.t(LOCALS.shop_id),
        dataIndex: 'shopid',
        key: 'shopid',
        render: (_: string, record: OmsRecycleOrderDetail) => {
          if (!record) return;
          return ShopMaping[`${record.omsRecycleOrder?.storeId}`] || '-';
        },
      },
      {
        title: i18n.t(LOCALS.staff),
        dataIndex: 'staffName',
        key: 'staffName',
        render: (_: string, record: OmsRecycleOrderDetail) => {
          if (!record) return;
          const { staffName, createBy } = record.omsRecycleOrder || {};
          if (staffName) return staffName;
          const fallbackStaffName = staffSelectOptions.find(
            (d) => Number(d.value) === Number(createBy),
          )?.label;
          return fallbackStaffName || '-';
        },
      },
      {
        title: i18n.t(LOCALS.order_status),
        dataIndex: 'status',
        key: 'status',
        render: (_: string, record: OmsRecycleOrderDetail) => {
          if (!record) return;
          return <div>{getRecycleOrderStatusText(record.omsRecycleOrder)}</div>;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.quoted_amount} />,
        dataIndex: 'price',
        key: 'price',
        render: (_: string, record: OmsRecycleOrderDetail) => {
          if (!record) return;
          if (record.omsRecycleOrderItem) {
            const {
              finalRecyclePrice = '',
              finalSalePrice = '',
              maxRecyclePrice = '',
              maxSalePrice = '',
              minRecyclePrice = '',
              minSalePrice = '',
            } = record.omsRecycleOrderItem;
            const currency = record?.omsRecycleOrder?.currency;
            const { type, status } = record?.omsRecycleOrder || {};
            const finalRecyclePriceEle = (
              <>
                <div>{i18n.t(LOCALS.recycling_confirmation_quote)}</div>
                <div>
                  {finalRecyclePrice ? (
                    <>
                      {currency}&nbsp;
                      {thousands(finalRecyclePrice)}
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              </>
            );
            const finalSalePriceEle = (
              <>
                <div>{i18n.t(LOCALS.consignment_confirmation_quote)}</div>
                <div>
                  {finalSalePrice ? (
                    <>
                      {currency}&nbsp;
                      {thousands(finalSalePrice)}
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              </>
            );
            // 交易完成
            if (status === OMS_RECYCLE_ORDER_STATUS_MAP.COMPLETE) {
              return (
                <>
                  {type === 2 && finalRecyclePriceEle}
                  {type === 1 && finalSalePriceEle}
                </>
              );
            }
            // 最终
            if (finalRecyclePrice || finalSalePrice) {
              return (
                <div>
                  {finalRecyclePriceEle}
                  {finalSalePriceEle}
                </div>
              );
            }
            // 初步
            return (
              <div>
                <div>{i18n.t(LOCALS.instant_sale)}</div>
                {minRecyclePrice || maxRecyclePrice ? (
                  <div>
                    {currency}&nbsp;
                    {thousands(minRecyclePrice)}~{thousands(maxRecyclePrice)}
                  </div>
                ) : (
                  '-'
                )}
                <div>{i18n.t(LOCALS.consignment_preliminary_valuation)}</div>
                {minSalePrice || maxSalePrice ? (
                  <div>
                    {currency}&nbsp;
                    {thousands(minSalePrice)}~{thousands(maxSalePrice)}
                  </div>
                ) : (
                  '-'
                )}
              </div>
            );
          }
        },
      },
      {
        title: i18n.t(LOCALS.created_time),
        dataIndex: 'createTime',
        key: 'createTime',
        render: (_: string, record: OmsRecycleOrderDetail) => {
          if (!record) return;
          return (
            <div>
              {dayjs(record.omsRecycleOrder?.createTime).format(
                'YYYY-MM-DD HH:mm',
              )}
            </div>
          );
        },
      },
    ];
  }, [ProductShowData, navigate, staffSelectOptions]);

  const forwardReceipt = useCallback(async () => {
    const b = selectedRows.every(
      (d) => d.omsRecycleOrder?.type === selectedRows[0].omsRecycleOrder?.type,
    );
    if (!b) {
      message.warning(i18n.t('consignment_orders_and'));
      return;
    }
    const prodList: ProductType[] = [];
    let orderInfo: PrintParamType | undefined = undefined;
    const firstData = await fetchRecycleOrderDetail({
      id: selectedRows[0].omsRecycleOrder?.id,
    });
    orderInfo = await getDempyouOrderInfo(selectedRows[0], {
      staffSelectOptions,
      countryOptions,
      idCertificate: member.idCertificate,
      currentStaff: userId,
      staffName: firstData.data.omsRecycleOrder?.staffName,
      payoutDetail: firstData.data.omsRecycleOrder?.payoutDetail,
    });
    selectedRows.forEach((d) => {
      const productDisplayInfo = getProductDisplayInfo({
        productInfo: {
          name: d.omsRecycleOrderProduct?.name || '',
          rankDescription: d.omsRecycleOrderProduct?.attrRankDesc || '',
          productCategoryId: d.omsRecycleOrderProduct?.productCategoryId || 0,
          stamp: d.omsRecycleOrderProduct?.attrStamp || '',
          rank: d.omsRecycleOrderProduct?.rank || '',
          color: d.omsRecycleOrderProduct?.attrColor || '',
          material: d.omsRecycleOrderProduct?.attrMaterial || '',
          hardware: d.omsRecycleOrderProduct?.attrHardware || '',
          accessory: d.omsRecycleOrderProduct?.attrAccessory || '',
          i18nList: d.i18nList || [],
        },
        lang: getDempyouLang(d.omsRecycleOrder?.storeId),
        options: {
          productCategoryCascaderOptions,
          materialCascaderOptionsMap,
          colorSelectOptions,
          rankSelectOptions,
          stampSelectOptions,
          hardwareSelectOptions,
          accessorySelectOptions,
        },
      });

      const productName = buildProductNameWithAttributeRemarks({
        name: productDisplayInfo.name,
        material: productDisplayInfo.material,
        materialRemark: d.omsRecycleOrderProduct?.attrMaterialRemark,
        color: productDisplayInfo.color,
        colorRemark: d.omsRecycleOrderProduct?.attrColorRemark,
        stamp:
          d.omsRecycleOrderProduct?.fullStamp || productDisplayInfo.stamp,
        stampRemark: d.omsRecycleOrderProduct?.attrStampRemark,
        hardware: productDisplayInfo.hardware,
        hardwareRemark: d.omsRecycleOrderProduct?.attrHardwareRemark,
        accessory: productDisplayInfo.accessory,
      });

      prodList.push(
        getDempyouProduct(d, {
          name: productName,
          rankDes: productDisplayInfo.rankDesc,
          rankSelectOptions,
        })!,
      );
    });
    if (prodList && orderInfo) {
      getDempyouToPrint({
        productList: prodList,
        printParam: orderInfo,
        prints: true,
      });
    }
  }, [
    accessorySelectOptions,
    colorSelectOptions,
    countryOptions,
    hardwareSelectOptions,
    materialCascaderOptionsMap,
    member.idCertificate,
    productCategoryCascaderOptions,
    rankSelectOptions,
    selectedRows,
    staffSelectOptions,
    stampSelectOptions,
    userId,
  ]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-base font-medium">
          <ShoppingBag className="w-5 h-5" />
          <Trans i18nKey={LOCALS.recycling_consignment_order_history} />
        </div>

        <div className="gap-2 hidden md:flex">
          <Select
            allowClear
            showSearch
            style={{ width: 220 }}
            placeholder={i18n.t(LOCALS.order_status) || ''}
            value={status}
            options={StatusListDefault.map((d) => ({
              label: d.label,
              value: d.key,
            }))}
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onChange={(value) => {
              const nextStatus = value ?? 'all';
              setStatus(nextStatus);
              setPageNum(1);
              getDataSource({
                pageNum: 1,
                pageSize,
                status: nextStatus === 'all' ? '' : nextStatus,
              });
            }}
          />
          <Select
            allowClear
            showSearch
            style={{ width: 220 }}
            placeholder={i18n.t(LOCALS.shop_id) || ''}
            value={storeId}
            options={storeSource}
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onChange={(value) => {
              setStoreId(value);
              setPageNum(1);
              getDataSource({
                pageNum: 1,
                pageSize,
                storeId: value,
                status: status === 'all' ? '' : status,
              });
            }}
          />
          <DatePicker.RangePicker
            style={{ width: 260 }}
            onChange={(value) => {
              if (value) {
                const begin = dayjs(value[0]).startOf('days').format();
                const end = dayjs(value[1]).endOf('days').format();
                setBeginCommitTime(begin);
                setEndCommitTime(end);
                setPageNum(1);
                getDataSource({
                  pageNum: 1,
                  pageSize,
                  beginCommitTime: begin,
                  endCommitTime: end,
                  status: status === 'all' ? '' : status,
                });
              } else if (value === null) {
                setBeginCommitTime('');
                setEndCommitTime('');
                setPageNum(1);
                getDataSource({
                  pageNum: 1,
                  pageSize,
                  beginCommitTime: '',
                  endCommitTime: '',
                  status: status === 'all' ? '' : status,
                });
              }
            }}
          />
          <Input
            placeholder={i18n.t(LOCALS.sHXnOEVSrC) || ''}
            value={submissionId === undefined ? '' : String(submissionId)}
            onChange={(e) => {
              const value = e.target.value;
              // 处理空值或数值输入
              if (value === '') {
                setSubmissionId(undefined);
              } else if (/^\d+$/.test(value)) {
                setSubmissionId(Number(value));
              }
            }}
            style={{ width: 200 }}
            onPressEnter={() => {
              setPageNum(1);
              getDataSource({
                pageNum: 1,
                pageSize,
                status: status === 'all' ? '' : status,
              });
            }}
          />
          <Button
            type="primary"
            disabled={!selectedRows.length}
            onClick={() => forwardReceipt()}
            className="flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            {i18n.t('yYqvdvcMFh')}
          </Button>
        </div>
      </div>

      <Table
        bordered
        pagination={{
          showTotal,
          total,
          pageSize,
          current: pageNum,
          showSizeChanger: true,
        }}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys: selectedRows.map(
            (d) => d.omsRecycleOrder?.code,
          ) as React.Key[],
          onChange: (_, selectedRows) => setSelectedRows(selectedRows),
          getCheckboxProps: (record: OmsRecycleOrderDetail) => ({
            disabled: !record.omsRecycleOrderLogistics?.country,
          }),
        }}
        loading={loading}
        rowKey={(e) => e.omsRecycleOrder?.code || ''}
        onChange={(pagination, filters, sorter, { action }) => {
          if (action === 'paginate') {
            const nextPageNum = pagination.current || 1;
            const nextPageSize = pagination.pageSize || pageSize;
            setPageNum(nextPageNum);
            setPageSize(nextPageSize);
            getDataSource({
              pageNum: nextPageNum,
              pageSize: nextPageSize,
              status: status === 'all' ? '' : status,
            });
          }
        }}
        dataSource={dataSource}
        columns={columns}
      />
    </Card>
  );
};

export default RecyclingConsignmentHistory;
